<template>
  <div>
    <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
      <div class="container-fluid d-flex align-items-center">
        <div class="row">
          <div class="col-lg-12">
            <h2 class="display-5 text-white">{{league ? league.Name : ''}}</h2>
            <h1 class="display-2 text-white">
              Week {{weekObject ? weekObject.WeekNumber : ''}} Schedule
            </h1>
          </div>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--7">
      <div class="row">
        <div class="col-md-4 teams-box" sticky-container>
          <card v-sticky>
            <div slot="header">
              <h3 class="mb-0">Teams</h3>
            </div>
            <draggable
              v-model="availableTeams"
              :group="{name: 'teams', }"
              class="scroll-container"
            >
              <TeamScheduleCard
                v-for="team in availableTeams"
                :key="'team'+team.id"
                :team="team"
                class="list-item"
                :week="weekObject"
              />
            </draggable>
          </card>
        </div>
        <div class="col-md-8">
          <card>
            <div slot="header" class="row">
              <div class="col d-flex align-items-center">
                <h3 class="mb-0">Matches</h3>
              </div>
              <div class="col-auto text-right d-flex align-items-center justify-content-end">
                <base-button
                  type="success"
                  size="sm"
                  @click="addMatch"
                >
                  Add Match
                </base-button>
              </div>
            </div>
            <MatchScheduleCard
              class="list-item"
              v-for="(match, index) in matches"
              :key="match.id || index"
              v-model="matches[index]"
              :week="weekObject"
              ref="matchCards"
              @onRemove="deleteMatch(match.id, index)"
              :single="league && league.type === 'mixed'"
            />
            <div class="row">
              <div class="col-12 text-right d-flex align-items-center justify-content-end">
                <base-button
                  type="danger"
                  @click="discardChanges"
                >
                  Discard Changes
                </base-button>
                <base-button
                  type="success"
                  @click="saveAllMatches"
                >
                  Save Schedule
                </base-button>
              </div>
            </div>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import draggable from 'vuedraggable';
import Sticky from 'vue-sticky-directive';
import TeamScheduleCard from '@/components/TeamScheduleCard.vue';
import MatchScheduleCard from '@/components/MatchScheduleCard.vue';
import ErrorHandler from '@/helpers/AuthError';

export default {
  name: 'LeagueScheduleBuilder',
  components: {
    TeamScheduleCard,
    MatchScheduleCard,
    draggable,
  },
  directives: { Sticky },
  props: {
    id: [String, Number],
    week: [String, Number],
  },
  data() {
    return {
      league: null,
      weekObject: null,
      teams: [],
      matches: [],
      assignedTeams: [],
      availableTeams: [],
      blankMatch: {
        matchDateTime: null,
        simulator: '',
        teams: [],
      },
    };
  },
  mounted() {
    this.getLeague();
    this.getWeek()
      .then(() => {
        this.blankMatch.matchDateTime = this.weekObject ? this.weekObject.Start : null;
      });
    this.getTeams()
      .then(() => this.getMatches());
  },
  watch: {
    teams() {
      this.getAvailableTeams();
    },
    assignedTeams() {
      this.getAvailableTeams();
    },
  },
  methods: {
    discardChanges() {
      this.$router.push({ name: 'view league', params: { id: this.id } });
    },
    saveAllMatches() {
      this.$refs.matchCards.forEach((card) => {
        if (card.status.text === 'UNSAVED') {
          card.save();
        }
      });
      this.getTeams()
        .then(() => this.getMatches())
        .then(() => {
          this.getAvailableTeams();
        });
    },
    deleteMatch(id, index) {
      if (!id) {
        this.matches.splice(index, 1);
        return;
      }
      this.axios
        .delete(`/matches/${id}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then(() => this.getTeams())
        .then(() => this.getMatches())
        .then(() => {
          this.getAvailableTeams();
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    getAvailableTeams() {
      const availableTeams = [];
      this.teams.forEach((team) => {
        if (!this.assignedTeams.includes(team.id)) {
          availableTeams.push(team);
        }
      });
      this.availableTeams = availableTeams;
    },
    addMatch() {
      this.matches.push({ ...this.blankMatch });
    },
    getLeague() {
      return this.axios
        .get(`/leagues/${this.id}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          console.log(response.data);
          this.league = response.data;
          this.league.weeks = this.league.weeks.sort((a, b) => a.WeekNumber - b.WeekNumber);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    getWeek() {
      return this.axios
        .get(`/weeks/${this.week}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          this.weekObject = response.data;
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    getTeams() {
      return this.axios
        .get(`/teams?league_eq=${this.id}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          this.teams = response.data;
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    getMatches() {
      return this.axios
        .get(`/matches?week_eq=${this.week}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          this.matches = response.data;

          const assignedTeams = [];
          this.matches.forEach((match, index) => {
            const newTeams = [];
            match.teams.forEach((team) => {
              assignedTeams.push(team.id);
              newTeams.push(this.getTeamFromTeams(team.id));
            });
            this.matches[index].teams = newTeams;
          });
          this.assignedTeams = assignedTeams;
          console.log(this.matches);
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    getTeamFromTeams(id) {
      let result = null;
      this.teams.forEach((team) => {
        if (team.id === id) {
          result = team;
        }
      });
      return result;
    },
  },
};
</script>
<style lang="scss" scoped>
  .list-item{
    margin-bottom: 1rem;

    &:last-child{
      margin-bottom: 0;
    }
  }

  .teams-box{
    margin-bottom: 1rem;
  }

  .scroll-container{
    max-height: 90vh;
    overflow-y: auto;
  }
</style>
